import { Alert, BoxProps, Stack, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { addYears, format, parseISO } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useBoolean, useLocalStorage } from 'usehooks-ts';

import { getHolderDetailAndRequiredDocumentsRequest } from '@/api/operation';
import { updateMinorHolderInfo, updateUserDocumentInfo } from '@/api/user';

import { DatePicker } from '@/components/Form/DatePicker';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';
import { Form } from '@/components/Layout';
import { MobileOnly } from '@/components/Layout/Responsive';

import { LS_USER_BLOCKED_AT_PATH } from '@/constants/common';
import {
  COUNTRIES_WITH_POPULAR,
  NATIONALITIES_WITH_POPULAR,
} from '@/constants/countries';
import { DATE_VALUE_FORMAT } from '@/constants/patterns';
import { ROUTE } from '@/constants/routes';
import { GENDERS, getUserTitleOptions } from '@/constants/user';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { usePrevious } from '@/hooks/usePrevious';
import { useTranslate } from '@/hooks/useTranslate';
import { useUpdateUser } from '@/hooks/useUpdateUser';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';
import { setOperationData } from '@/redux/operation';
import { selectOperationUser } from '@/redux/operation/selectors';
import { selectUserData, selectUserHolderJointMinor } from '@/redux/user-data';

import { Option } from '@/types/components/common';
import { DocumentSection, DocumentType } from '@/types/document';
import { Gender, MaritalStatus } from '@/types/user';

import {
  getAPIErrorMessage,
  getAPIFormError,
  isMinorAgeRestrictionError,
} from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { getAllowedIdentityTypes } from '@/utils/configs';
import { findLatestActiveDoc } from '@/utils/document';
import { isPastDate } from '@/utils/validation';

type MinorBasicInfoFormFields = {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  motherMaidenName: string;
  gender: string;
  idDocType: string;
  idDocNumber: string;
  idExpiryDate: string;
  issueDate: string;
  issueCountry: string;
  citizenship: string;
  countryOfBirth: string;
  birthday: string;
};

type MinorBasicInfoFormProps = BoxProps;

export function MinorBasicInfoForm(props: MinorBasicInfoFormProps) {
  const { t, nationality, country } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const minorUser = useAppSelector(selectOperationUser);
  const adultUser = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const { accountTypes } = useAppSelector(selectUserData);
  const { features } = useAppSelector(selectConfigsState);
  const updateUser = useUpdateUser();
  const { value: isRefreshing, setValue: setRefreshing } = useBoolean(false);
  const [, setUserBlockedPath] = useLocalStorage(LS_USER_BLOCKED_AT_PATH, '');

  const {
    dateFormat,
    idDocumentTypes,
    minorOnlyIdDocumentTypes,
    defaultIdentityType,
    idTypesWithoutExpiryDate,
    isoCode,
  } = useAppSelector(selectConfigsState);
  const theme = useTheme();

  const allowedIdentityTypes = useMemo(
    () =>
      getAllowedIdentityTypes(
        [...idDocumentTypes, ...(minorOnlyIdDocumentTypes ?? [])],
        features,
      ).map(
        docType =>
          ({
            value: docType,
            text: t(`documentType.${docType}`) || docType,
          }) as Option,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idDocumentTypes, minorOnlyIdDocumentTypes, features],
  );

  const idDocument = findLatestActiveDoc(
    minorUser?.documents ?? [],
    DocumentSection.Identity,
  );
  const isFamilyBook =
    isoCode === 'SUR' && idDocument?.docType === DocumentType.FamilyBook;
  const isNewFamilyBook =
    isFamilyBook &&
    !minorUser?.documents.some(
      doc =>
        doc.type === DocumentSection.Identity &&
        doc.docType === DocumentType.FamilyBook &&
        doc.infoConfirmed,
    );

  const nationalityOptions = useMemo(
    () =>
      NATIONALITIES_WITH_POPULAR.map(item => ({
        ...item,
        text: nationality[item.value],
      })),
    [nationality],
  );

  const countryOptions = useMemo(
    () =>
      COUNTRIES_WITH_POPULAR.map(item => ({
        ...item,
        text: country[item.value],
      })),
    [country],
  );

  const methods = useForm<MinorBasicInfoFormFields>({
    defaultValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      motherMaidenName: '',
      title: '',
      gender: '',
      citizenship: '',
      countryOfBirth: '',
      birthday: '',
      idDocNumber: '',
      idExpiryDate: '',
      issueDate: '',
      issueCountry: '',
      idDocType: defaultIdentityType || '',
    },
    mode: 'all',
    shouldUnregister: false,
  });

  const titleOptions = useMemo(
    () => getUserTitleOptions(adultUser?.tenant),
    [adultUser?.tenant],
  );

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    watch,
    setValue,
  } = methods;

  // Compute initial form values from minor user data
  const initialFormValues = useMemo(() => {
    if (!minorUser) {
      return undefined;
    }
    return {
      firstName: isNewFamilyBook ? '' : minorUser.firstName || '',
      middleName: isNewFamilyBook ? '' : minorUser.middleName || '',
      lastName: isNewFamilyBook ? '' : minorUser.lastName || '',
      motherMaidenName: isNewFamilyBook ? '' : minorUser.motherMaidenName || '',
      title: isNewFamilyBook ? '' : minorUser.title || '',
      gender:
        !isNewFamilyBook &&
        minorUser.gender &&
        [Gender.Female, Gender.Male].includes(minorUser.gender as Gender)
          ? minorUser.gender
          : '',
      citizenship:
        !isNewFamilyBook &&
        minorUser.citizenship &&
        nationality[minorUser.citizenship]
          ? minorUser.citizenship
          : isNewFamilyBook
            ? ''
            : idDocument?.issueCountry || '',
      countryOfBirth:
        !isNewFamilyBook &&
        minorUser.countryOfBirth &&
        countryOptions.some(type => type.value === minorUser.countryOfBirth)
          ? minorUser.countryOfBirth
          : '',
      birthday: isNewFamilyBook ? '' : minorUser.birthday || '',
      idDocType: isFamilyBook
        ? DocumentType.FamilyBook
        : idDocument?.docType &&
            allowedIdentityTypes.some(type => type.value === idDocument.docType)
          ? idDocument.docType
          : defaultIdentityType || '',
      idDocNumber: isNewFamilyBook ? '' : idDocument?.docNumber || '',
      idExpiryDate: idDocument?.expiryDate || '',
      issueDate: idDocument?.issueDate || '',
      issueCountry: idDocument?.issueCountry || (isFamilyBook ? isoCode : ''),
    };
    // Values are derived from the minor record; options and translations are
    // stable for the lifetime of that record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minorUser, isoCode]);

  // Set form values when initial values are computed
  useEffect(() => {
    if (initialFormValues) {
      Object.entries(initialFormValues).forEach(([key, value]) => {
        setValue(key as keyof MinorBasicInfoFormFields, value as string);
      });
    }
  }, [initialFormValues, setValue]);

  // Only set idDocType if it's not already set
  useEffect(() => {
    const currentIdDocType = watch('idDocType');
    if (
      !currentIdDocType &&
      idDocument?.docType &&
      allowedIdentityTypes.some(type => type.value === idDocument.docType)
    ) {
      setValue('idDocType', idDocument.docType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedIdentityTypes, idDocument?.docType]);

  const idDocType = watch('idDocType') as string;
  const birthday = watch('birthday');
  const prevIdDocType = usePrevious(idDocType);

  useEffect(() => {
    if (!isFamilyBook) return;
    setValue('issueDate', birthday || '', { shouldValidate: !!birthday });
    setValue(
      'idExpiryDate',
      birthday
        ? format(addYears(parseISO(birthday), 16), DATE_VALUE_FORMAT)
        : '',
      { shouldValidate: !!birthday },
    );
  }, [birthday, isFamilyBook, setValue]);

  const {
    mutate,
    isPending: isMutating,
    error,
    reset,
  } = useMutation({
    mutationFn: async (
      payload: MinorBasicInfoFormFields & {
        maritalStatus?: MaritalStatus;
      },
    ) => {
      if (!adultUser?.operationId || !holderJointMinor?.clientId) {
        throw new Error('Missing operationId or clientId');
      }
      const { issueDate, issueCountry, motherMaidenName, ...holderInfo } =
        payload;
      await updateMinorHolderInfo({
        operationId: adultUser.operationId,
        clientId: holderJointMinor.clientId,
        payload: isFamilyBook
          ? { ...holderInfo, motherMaidenName }
          : holderInfo,
      });

      if (isFamilyBook) {
        if (!idDocument?.id) {
          throw new Error('Missing Family Book document');
        }
        await updateUserDocumentInfo({
          documentId: idDocument.id,
          payload: {
            idDocNumber: payload.idDocNumber,
            idDocType: DocumentType.FamilyBook,
            idExpiryDate: payload.idExpiryDate,
            issueDate,
            issueCountry,
            issueCity: null,
          },
        });
      }

      await updateUser();
      const data = await getHolderDetailAndRequiredDocumentsRequest(
        adultUser.operationId,
        holderJointMinor.clientId,
      );
      dispatch(
        setOperationData({
          user: data.user || null,
          requiredSections: data.requiredSections || [],
          accountTypes: accountTypes || [],
        }),
      );
    },
    onSuccess: () => {
      setRefreshing(false);
      navigate(ROUTE.MORE_INFORMATION, { replace: true });
    },
    onError: () => {
      setRefreshing(false);
    },
  });

  const isPending = isRefreshing || isMutating;

  const apiError = getAPIFormError<MinorBasicInfoFormFields>(error);

  useEffect(() => {
    if (isMinorAgeRestrictionError(error)) {
      setUserBlockedPath(ROUTE.MINOR_AGE_VALIDATION_FAILURE);
      navigate(ROUTE.MINOR_AGE_VALIDATION_FAILURE);
    }
  }, [error, navigate, setUserBlockedPath]);

  const onSubmit = handleSubmit(async payload => {
    reset();
    setRefreshing(true);
    mutate({ ...payload, maritalStatus: MaritalStatus.Single });
  });

  useEffect(() => {
    if (
      !isFamilyBook &&
      prevIdDocType &&
      idTypesWithoutExpiryDate?.includes(prevIdDocType) &&
      !idTypesWithoutExpiryDate?.includes(idDocType)
    ) {
      setValue('idExpiryDate', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDocType, isFamilyBook]);

  const genderOptions = useMemo(
    () =>
      GENDERS.map(gen => ({
        ...gen,
        text: t(String(gen.i18nKey)),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit} {...props}>
        <Select
          options={titleOptions}
          control={control}
          name="title"
          error={!!errors.title}
          helperText={errors.title?.message}
          rules={{
            required: t('createNewAccount.basicInfo.form.title.error.required'),
          }}
          fullWidth
          disabled={isPending}
          placeholder={t('createNewAccount.basicInfo.form.title.placeHolder')}
          label={t('createNewAccount.basicInfo.form.title.label')}
        />
        <TextInput
          name="firstName"
          control={control}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.firstName.error.required',
            ),
          }}
          disabled={isPending}
          fullWidth
          label={t('createNewAccount.basicInfo.form.firstName.label')}
        />
        <TextInput
          name="middleName"
          control={control}
          disabled={isPending}
          fullWidth
          label={t('createNewAccount.basicInfo.form.middleName.label')}
        />
        <TextInput
          name="lastName"
          control={control}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.lastName.error.required',
            ),
          }}
          disabled={isPending}
          fullWidth
          label={t('createNewAccount.basicInfo.form.lastName.label')}
        />
        {isFamilyBook && (
          <TextInput
            name="motherMaidenName"
            control={control}
            disabled={isPending}
            fullWidth
            label={t('createNewAccount.basicInfo.form.motherMaidenName.label')}
          />
        )}
        <Select
          options={genderOptions}
          control={control}
          name="gender"
          error={!!errors.gender}
          helperText={errors.gender?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.gender.error.required',
            ),
          }}
          fullWidth
          disabled={isPending}
          placeholder={t('createNewAccount.basicInfo.form.gender.placeHolder')}
          label={t('createNewAccount.basicInfo.form.gender.label')}
        />

        <Select
          options={allowedIdentityTypes}
          control={control}
          name="idDocType"
          error={!!errors.idDocType}
          helperText={errors.idDocType?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.idDocType.error.required',
            ),
          }}
          fullWidth
          disabled={isPending || isFamilyBook}
          placeholder={t(
            'createNewAccount.basicInfo.form.idDocType.placeHolder',
          )}
          label={t('createNewAccount.basicInfo.form.idDocType.label')}
        />
        <TextInput
          name="idDocNumber"
          control={control}
          error={!!errors.idDocNumber || !!apiError.idDocNumber}
          helperText={errors.idDocNumber?.message || apiError.idDocNumber}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.idDocNumber.error.required',
            ),
          }}
          disabled={isPending}
          fullWidth
          label={t(
            isFamilyBook
              ? 'verification.verifyMinorID.familyBook.numberLabel'
              : 'createNewAccount.basicInfo.form.idDocNumber.label',
          )}
        />
        {isFamilyBook && (
          <>
            <Select
              options={countryOptions}
              control={control}
              name="issueCountry"
              error={!!errors.issueCountry}
              helperText={errors.issueCountry?.message}
              rules={{
                required: t(
                  'createNewAccount.basicInfo.form.issueCountry.error.required',
                ),
              }}
              fullWidth
              disabled={isPending}
              useDrawerOnMobile
              withSearchBar
              label={t('createNewAccount.basicInfo.form.issueCountry.label')}
            />
            <DatePicker
              name="issueDate"
              label={t('createNewAccount.basicInfo.form.idIssueDate.label')}
              format={dateFormat}
              control={control}
              error={!!errors.issueDate}
              helperText={errors.issueDate?.message}
              rules={{
                required: t(
                  'createNewAccount.basicInfo.form.idIssueDate.error.required',
                ),
              }}
              disabled
            />
          </>
        )}
        {!idTypesWithoutExpiryDate?.includes(idDocType) && (
          <DatePicker
            name="idExpiryDate"
            label={t('createNewAccount.basicInfo.form.idExpiryDate.label')}
            format={dateFormat}
            control={control}
            error={!!errors.idExpiryDate}
            helperText={errors.idExpiryDate?.message}
            rules={{
              required: t(
                'createNewAccount.basicInfo.form.idExpiryDate.error.required',
              ),
              ...(!isFamilyBook && {
                validate: {
                  mustBeTodayOrFuture: (value: string) =>
                    isPastDate(value)
                      ? t(
                          'createNewAccount.basicInfo.form.idExpiryDate.error.mustBeTodayOrFuture',
                        )
                      : undefined,
                },
              }),
            }}
            disabled={isPending || isFamilyBook}
          />
        )}
        <Select
          options={nationalityOptions}
          control={control}
          name="citizenship"
          error={!!errors.citizenship}
          helperText={errors.citizenship?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.citizenship.error.required',
            ),
          }}
          fullWidth
          disabled={isPending}
          placeholder={t(
            'createNewAccount.basicInfo.form.citizenship.placeHolder',
          )}
          useDrawerOnMobile
          label={t('createNewAccount.basicInfo.form.citizenship.label')}
          withSearchBar
        />
        <Select
          options={countryOptions}
          control={control}
          name="countryOfBirth"
          error={!!errors.countryOfBirth}
          helperText={errors.countryOfBirth?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.countryOfBirth.error.required',
            ),
          }}
          fullWidth
          disabled={isPending}
          placeholder={t(
            'createNewAccount.basicInfo.form.countryOfBirth.placeHolder',
          )}
          useDrawerOnMobile
          label={t('createNewAccount.basicInfo.form.countryOfBirth.label')}
          withSearchBar
        />
        <DatePicker
          name="birthday"
          label={t('createNewAccount.basicInfo.form.dateOfBirth.label')}
          format={dateFormat}
          control={control}
          error={!!errors.birthday}
          helperText={errors.birthday?.message}
          rules={{
            required: t(
              'createNewAccount.basicInfo.form.dateOfBirth.error.required',
            ),
          }}
          disabled={isPending}
          disableFuture
        />

        {error && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}
        <LoadingButton
          {...getDataTestId('minor-basic-info-form-confirm-button')}
          type="submit"
          fullWidth
          isLoading={isPending}
          disabled={!isValid || isPending}
        >
          {t('createNewAccount.basicInfo.form.button.submit')}
        </LoadingButton>
        <MobileOnly>
          <Stack direction="row" justifyContent="center" gap={1.5} mt={0.5}>
            <Icon
              name="lock"
              color={(theme: Theme) => theme.palette.grey[700]}
            />
            <Typography variant="caption" color={theme.palette.grey[800]}>
              {t('createNewAccount.basicInfo.form.protectData')}
            </Typography>
          </Stack>
        </MobileOnly>
      </Form>
    </FormProvider>
  );
}
